import { HOME_MSG, NAV_GROUP_MSG, PURCHASE_MSG, RFQ_MSG, ACCOUNT_MENU_MSG, ACCOUNT_NAME_MSG, COVER_LETTER_MSG, NEW_COMPANY_MSG, PC_ONLY_NOTE, ROLE_INFO, STOP_MSG, SWITCHER_MSG, fullName, planSpec, type BillingStatus, type Me } from "@genbaryoku/contract";
import { useMailNotices, useOrderNotices, usePaymentNoticeNotices } from "./MailNotices.jsx";
import { useLogoHeader } from "./LightArea.jsx";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate, type NavLinkProps } from "react-router";
import { useLogout } from "../auth.js";
import { setCurrentLabels, t } from "../labels.js";
import { applyBranding, productInfo, redirectToProductHost, setCurrentProduct, warnProductMismatch } from "../lib/product.js";
import { SiteFooter } from "./ui.jsx";
import { TextSize } from "./TextSize.jsx";
import { ViewSwitchRow } from "./ViewSwitch.jsx";
import { CompanyIcon } from "./CompanyIcon.jsx";
import { GroupDangerRow, GroupList, GroupNavRow, GroupValueRow, RowIcon } from "./GroupList.jsx";
import { PersonIcon } from "./PersonIcon.jsx";
import { Sheet } from "./Sheet.jsx";
import { currentView } from "../lib/view.js";
import { usePcOnly } from "../lib/pcOnly.js";
import { repaintOnReturn } from "../lib/repaint.js";
import { navAfter } from "../core/slots.jsx";
import { uses } from "../core/uses.js";
import { PasskeyPrompt } from "./Passkey.jsx";
import { SearchBox } from "./SearchBox.jsx";
import { HelpNavButton, useSupport } from "./Support.jsx";
import { AreaIcon, areaOfPath, type Area } from "./AreaIcon.jsx";
import { UpdatesDialog, UpdatesNavButton, useUpdates } from "./Updates.jsx";
import { ColorModeRow, ThemeColorRow } from "./ThemeColor.jsx";

type IconKey = "home" | "doc" | "inv" | "user" | "gear" | "plus" | "lock" | "more" | "chev" | "search" | "pen" | "clock" | "folder" | "yen" | "building" | "card" | "switch" | "logout" | "arrow" | "check" | "cal" | "gantt" | "text" | "x" | "vq" | "send" | "po" | "box" | "truck";
/** アイコン（24×24 の線。太さ・色は置いた場所の CSS。9/29 に screen_sample/design_2026-09-29/home.html の形に） */
function Icon({ k }: { k: IconKey }) {
  const p = {
    home: <path d="M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />,
    folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
    gantt: <path d="M4 4v16h16M8 8h6M10 12h7M8 16h4" />,
    doc: <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM14 3v5h5M9 13h6M9 17h6" />,
    /** 外注見積（#198）：届いた紙に入る矢印 */
    vq: <path d="M9 3h6l5 5v13a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-6M15 3v5h5M3 12h9M9 9l3 3-3 3" />,
    inv: <path d="M5 3h14v18l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21zM9 8h6M9 12h6M9 16h4" />,
    /** {order}（#233）：紙挟みに ✓（承諾） */
    po: <path d="M9 4h6v3H9zM9 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-3M9 14l2 2 4-4" />,
    /** {deliveryNote}（#266）：トラック（AreaIcon の deliveryNotes と同じ形） */
    truck: (
      <>
        <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2M15 18H9M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
        <circle cx="17" cy="18" r="2" />
        <circle cx="7" cy="18" r="2" />
      </>
    ),
    /** 仕入（#233 3a）：箱 */
    box: (
      <>
        <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
        <path d="M3 8l9 5 9-5M12 13v8" />
      </>
    ),
    /** 送付状（#223 の3回目の前の直し、docs/89 ★12）：封筒 */
    send: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 7l9 6 9-6" />
      </>
    ),
    yen: (
      <>
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="3" />
        <path d="M6 12h.01M18 12h.01" />
      </>
    ),
    building: <path d="M3 21h18M5 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M15 9h3a1 1 0 0 1 1 1v11M9 8h2M9 12h2M9 16h2" />,
    card: (
      <>
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
      </>
    ),
    switch: <path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3" />,
    logout: <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />,
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    check: <path d="M20 6L9 17l-5-5" />,
    cal: (
      <>
        <rect x="3" y="4" width="18" height="17" rx="2" />
        <path d="M3 9h18M8 2v4M16 2v4" />
      </>
    ),
    pen: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    gear: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    x: <path d="M6 6l12 12M18 6L6 18" />,
    text: <path d="M4 7V5h11v2M9.5 5v14M7 19h5M14 12v-1.5h7V12M17.5 10.5V19M16 19h3" />,
    lock: (
      <>
        <rect x="5" y="11" width="14" height="10" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </>
    ),
    more: <path d="M4 6h16M4 12h16M4 18h10" />,
    chev: <path d="M6 9l6 6 6-6" />,
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-4-4" />
      </>
    ),
  }[k];
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {p}
    </svg>
  );
}

/** 「10月20日」（日本時間） */
export function jmonthDay(iso: string): string {
  const j = new Date(new Date(iso).getTime() + 9 * 3600 * 1000);
  return `${j.getUTCMonth() + 1}月${j.getUTCDate()}日`;
}
/** 無料トライアルの最後の日（trialEndsAt はその翌日の 0:00 なので 1ミリ秒戻す） */
export function trialLastDay(iso: string): string {
  return jmonthDay(new Date(new Date(iso).getTime() - 1).toISOString());
}
/** 「無料トライアル あと12日」／最後の日は「きょうまで」 */
export function trialText(daysLeft: number | null): string {
  return daysLeft === 0 ? "無料トライアル きょうまで" : `無料トライアル あと${daysLeft ?? 0}日`;
}

/**
 * 右上の「アカウント」メニュー（縦串11、034、docs/26 §1 → #152、docs/61 §2・★f・★g、Figma「アカウントと氏名（10/8）」①①b②）。
 *   ボタン：氏名があれば 頭文字の丸（姓の1文字）＋「姓 名」＋▾、無ければ 人の形＋メール（狭い幅は丸だけ）。開いている間は地 #E7EEF4・▾ が 180°。
 *   中身（iOS のまとまり。components/GroupList.tsx の設定の行）：
 *     ① 頭（人のアイコン 48・氏名 太字・メール・›）＝個人設定（「個人設定」の行は無くした。氏名が無ければ藍の太字「氏名登録」）
 *     ② いまの会社（CompanyIcon・会社名・権限・「切替 ›」＝会社の切替。会社名は「…」で切り、「切替」は幅を固定。1社は › だけ。S-00009 の確かめ）＋「会社を追加」
 *     ③ 料金プラン（管理者だけ）④ 文字サイズ（セグメント。押してもメニューは閉じない）・PC版／スマホ版（出す条件は今のまま）⑤ ログアウト（朱）
 *   PC版＝ボタンの下から 380px のパネル（地は机・角丸 16・影 --shadow-hover）。スマホ版＝下から出るシート（頭に「アカウント」と ×。☰ の左のメニューとは別）。
 *   キーボード（docs/26 §6 のまま）：↓↑ で移る・Home／End・Esc で閉じてボタンに戻る・Tab で閉じる。外を押しても閉じる。押すところは 44px 以上
 */
function AccountMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const acc = me.account;
  const named = !acc.needsName;
  const who = named ? fullName(acc) : me.user.email;
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const loc = useLocation();
  const phone = currentView() === "phone";

  // 画面が変わったら閉じる。外を押しても閉じる（PC版のパネル。スマホ版のシートは後ろを押すと閉じる）
  useEffect(() => setOpen(false), [loc.pathname, loc.search]);
  useEffect(() => {
    if (!open || phone) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, phone]);

  const items = () => Array.from(document.getElementById(menuId)?.querySelectorAll<HTMLElement>('[role="menuitem"],[role="menuitemradio"]') ?? []);
  const openAndFocus = (last = false) => {
    setOpen(true);
    // スマホ版のシートは上がる動き（220ms）のあいだに focus を入れる（シートの最初の欄 → ここで最初の項目へ）
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const xs = items();
        (last ? xs[xs.length - 1] : xs[0])?.focus({ preventScroll: true });
      }),
    );
  };
  const close = (focusButton: boolean) => {
    setOpen(false);
    if (focusButton) requestAnimationFrame(() => btn.current?.focus());
  };

  const body = <AccountMenuBody me={me} id={menuId} onClose={close} />;
  return (
    <div className="acct" ref={wrap}>
      <button
        type="button"
        ref={btn}
        className={"acctbtn" + (open ? " is-open" : "")}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openAndFocus())}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            openAndFocus();
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            openAndFocus(true);
          }
        }}
        data-testid="acct-button"
      >
        {/* 頭文字の丸（姓の1文字。氏名が無ければ人の形）＋氏名（無ければメール）＋矢印。読み上げは「氏名 アカウント」 */}
        <PersonIcon familyName={named ? acc.familyName : ""} givenName={named ? acc.givenName : ""} size={36} />
        <span className="user">{who}</span>
        <span className="vh">{ACCOUNT_MENU_MSG.account}</span>
        <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {phone ? (
        <Sheet open={open} title={ACCOUNT_MENU_MSG.account} closeLabel={ACCOUNT_MENU_MSG.close} onCancel={() => close(true)} testId="acct-sheet" closeTestId="acct-sheet-close">
          {body}
        </Sheet>
      ) : open ? (
        <div className="acctmenu">{body}</div>
      ) : null}
    </div>
  );
}

/** メニューの中身（PC版のパネルとスマホ版のシートで同じ）。role="menu"・↓↑ で移る */
function AccountMenuBody({ me, id, onClose }: { me: Me; id: string; onClose: (focusButton: boolean) => void }) {
  const acc = me.account;
  const named = !acc.needsName;
  const logout = useLogout();
  const nav = useNavigate();
  const sizeId = useId();
  const others = me.companies.length - 1;
  const done = () => onClose(false);
  // PC 専用の機能（#185、docs/74 §1）：スマホでは「新しい会社を作る」「料金プラン」を出さず、一番下に1行
  const pcOnly = usePcOnly();
  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const xs = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"],[role="menuitemradio"]'));
    const i = xs.indexOf(document.activeElement as HTMLElement);
    const go = (j: number) => {
      e.preventDefault();
      xs[(j + xs.length) % xs.length]?.focus();
    };
    if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(xs.length - 1);
    else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose(true);
    } else if (e.key === "Tab") onClose(false);
  };
  return (
    <div className="amenu" id={id} role="menu" aria-label={ACCOUNT_MENU_MSG.account} onKeyDown={onMenuKey} data-testid="acct-menu">
      {/* ① 頭：押すと個人設定（★g） */}
      <GroupList className="am-head">
        <GroupNavRow
          menu
          icon={<PersonIcon familyName={named ? acc.familyName : ""} givenName={named ? acc.givenName : ""} size={48} />}
          title={named ? fullName(acc) : <span className="am-reg">{ACCOUNT_NAME_MSG.register}</span>}
          sub={me.user.email}
          to="/account/profile"
          onClick={done}
          buttonTestId="menu-profile"
        />
      </GroupList>
      {/* ② いまの会社（押すと会社の切替）＋会社を追加（#99 → #151 ★h：いつも出す） */}
      <GroupList className="am-co">
        <GroupNavRow
          menu
          icon={<CompanyIcon name={me.tenant.name} logoUrl={me.companies.find((c) => c.current)?.logoUrl ?? null} />}
          title={me.tenant.name || <span className="co-noname">{SWITCHER_MSG.noName}</span>}
          sub={ROLE_INFO[me.user.role].label}
          value={others > 0 ? ACCOUNT_MENU_MSG.switchCompany : undefined}
          valueTestId="menu-company-switch"
          to="/choose?switch"
          onClick={done}
          buttonTestId="menu-company"
          className="am-curco"
        />
        {pcOnly.hidden("/choose?switch&new") ? null : (
        <li className="grow grow-set has-co co-addrow">
          <Link className="growin" role="menuitem" to="/choose?switch&new" onClick={done} data-testid="menu-add-company">
            <span className="gtx">
              <span className="gtt">{NEW_COMPANY_MSG.menu}</span>
            </span>
            <span className="gico">
              <span className="co-plus" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </span>
            </span>
          </Link>
        </li>
        )}
      </GroupList>
      {/* ③ 料金プラン（管理者だけ・PC だけ） */}
      {me.user.role === "admin" && !pcOnly.hidden("/account/plan") ? (
        <GroupList>
          <GroupNavRow menu icon={<RowIcon k="card" />} title={ACCOUNT_MENU_MSG.plan} value={planSpec(me.billing.plan, me.tenant.product).label} to="/account/plan" onClick={done} buttonTestId="menu-plan" />
        </GroupList>
      ) : null}
      {/* ④ 表示：文字サイズ（★i。人に保存＝別の端末でも同じ。押してもメニューは閉じない）・テーマ色と明るさ（#191。10/16 夜 射場さん「メニューを広げてもテーマが無い」＝個人設定と同じ行）・PC版／スマホ版（#120。出す条件は今のまま） */}
      <GroupList>
        <GroupValueRow icon={<RowIcon k="text" />} title={ACCOUNT_MENU_MSG.textSize} titleId={sizeId} value={<TextSize menu labelledBy={sizeId} />} className="am-size" />
        <ThemeColorRow menu />
        <ColorModeRow menu />
        <ViewSwitchRow menu />
      </GroupList>
      {/* ⑤ ログアウト（朱） */}
      <GroupList>
        <GroupDangerRow
          menu
          title={ACCOUNT_MENU_MSG.logout}
          disabled={logout.isPending}
          onClick={() => logout.mutate(undefined, { onSettled: () => nav("/login", { replace: true }) })}
          buttonTestId="menu-logout"
        />
      </GroupList>
      {/* スマホ：PC 専用の機能の案内（#185。一覧は contract の pcOnly.ts） */}
      {pcOnly.phone ? (
        <p className="am-pcnote" data-testid="pc-only-note">
          {PC_ONLY_NOTE}
        </p>
      ) : null}
    </div>
  );
}

/** 無料トライアルのしるし（上の帯と ☰ の中）。料金プランは PC 専用（#185）＝スマホでは押せないしるし（リンクにしない） */
function TrialChip({ b, title, onClick }: { b: BillingStatus; title?: string; onClick?: () => void }) {
  const pcOnly = usePcOnly();
  if (pcOnly.hidden("/account/plan")) return <span className="chip ai trialchip">{trialText(b.daysLeft)}</span>;
  return (
    <Link className="chip ai trialchip" to="/account/plan" title={title} onClick={onClick}>
      {trialText(b.daysLeft)}
    </Link>
  );
}

/** 左のメニュー・☰ の項目（PC 専用の機能の一覧に道があれば、スマホでは出さない。#185） */
function NavItem(props: NavLinkProps & { to: string }) {
  const pcOnly = usePcOnly();
  return pcOnly.hidden(props.to) ? null : <NavLink {...props} />;
}

/**
 * ボタンの波紋（9/29、docs/10 §4.5）：document の mousedown を1つだけ見て、.btn なら押した座標に span.rip を足し 600ms で消す。
 * 「押せた」の合図（画面が切り替わるまでの間をつなぐ）。インラインの script は書かない（CSP）。動きを減らす設定なら出さない
 */
function useRipple() {
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const onDown = (e: MouseEvent) => {
      if (e.button !== 0 || reduce?.matches) return;
      const b = (e.target as Element | null)?.closest?.(".btn");
      if (!(b instanceof HTMLElement) || b.matches(":disabled,[aria-disabled='true']")) return;
      const r = b.getBoundingClientRect();
      const size = Math.max(r.width, r.height) * 2;
      const rip = document.createElement("span");
      rip.className = "rip";
      rip.setAttribute("aria-hidden", "true");
      rip.style.left = `${e.clientX - r.left - size / 2}px`;
      rip.style.top = `${e.clientY - r.top - size / 2}px`;
      rip.style.width = rip.style.height = `${size}px`;
      b.appendChild(rip);
      window.setTimeout(() => rip.remove(), 600);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
}

/** メニューの数のしるし（#106、docs/10 §9.12 の5）。工程表の「協力会社からの報告」の確かめ待ち（docs/39 §6）と、更新情報の未読（#204。#206 から「お知らせ」に朱の点） */
export type NavBadges = { schedule?: number; updates?: number };

/**
 * メニューの上の段＝業務の項目（左の列と、スマホの ☰ のメニューで同じもの・同じ順番。製品やオプションで出す・出さないも同じ）。
 * 2026-10-21 射場さん（docs/10 §9.27・docs/89 ★20）：「設定」は下の段（NavSettingsItem）へ
 * #242（docs/94 B-7・★6、10/26 射場さん）：4つの組に区切る＝［ホーム・{project}・{schedule}］—「受注」［見積書・請求書・入金］—
 *   「発注」［{vendorQuote}・見積依頼・{order}・仕入］—［{customer}・送付状］。見出し「受注」「発注」は広げたときだけ（畳んだ形は区切り線だけ）。
 *   組の中の項目が1つも出ない会社は組ごと出さない。現場力・営業力とも同じ並び（営業力の{order}も発注の組）。
 *   見積依頼（/rfqs）を足した（使う機能 rfq の会社だけ）
 * #240（docs/94 B-2）：メニューに項目の無い画面（領収書・{paymentNotice}・送付の記録など）は上の画面の分野の項目に印（areaOfPath）
 */
function NavItems({ me, badges }: { me: Me; badges: NavBadges }) {
  const L = me.tenant.labels;
  const area = areaOfPath(useLocation().pathname);
  const onA = (a: Area) => ({ isActive }: { isActive: boolean }) => (isActive || area === a ? "on" : "");
  const on = ({ isActive }: { isActive: boolean }) => (isActive ? "on" : "");
  const n = badges.schedule ?? 0;
  const sales = me.tenant.modules.includes("sales");
  /** 組（見出しは広げたときだけ。見出しの無い組は区切り線だけ）。中の項目が1つも無ければ出さない */
  const group = (key: string, label: string, items: ReactNode[]) => {
    const shown = items.filter(Boolean);
    if (!shown.length) return null;
    return (
      <div className="navgrp" role="group" aria-label={label || undefined} data-testid={`nav-group-${key}`}>
        {label ? (
          <div className="navgrp-h" aria-hidden="true">
            {label}
          </div>
        ) : null}
        {shown}
      </div>
    );
  };
  return (
    <>
      <NavItem to="/home" end className={on} title={HOME_MSG.menu}>
        <Icon k="home" />
        <span data-t={HOME_MSG.menu}>{HOME_MSG.menu}</span>
      </NavItem>
      {/* 案件（039、docs/28）：ホームの下。言葉は会社の設定（「現場」など） */}
      <NavItem to="/groups" className={({ isActive }) => ((isActive && area !== "schedule") || area === "groups" ? "on" : "")} title={L.project}>
        <Icon k="folder" />
        <span data-t={L.project}>{L.project}</span>
      </NavItem>
      {/* 工程表（#102、docs/39 §5.2 の1）：{project}の下。モジュール schedule の会社だけ（門は API の scheduleAvailable と同じ Me.features.schedule）。
          協力会社からの報告の確かめ待ちがあれば、朱の丸に件数（#103、docs/39 §6.2 の10） */}
      {me.features.schedule ? (
        <NavItem to="/schedule" className={onA("schedule")} title={L.schedule} data-testid="nav-schedule">
          <Icon k="gantt" />
          <span data-t={L.schedule}>{L.schedule}</span>
          {n > 0 ? (
            <i className="nbadge" data-testid="nav-schedule-badge" aria-label={t(`{contractor}からの報告 ${n}件`)}>
              {n > 99 ? "99+" : n}
            </i>
          ) : null}
        </NavItem>
      ) : null}
      {group("receive", NAV_GROUP_MSG.receive, [
        <NavItem key="estimates" to="/estimates" className={on} title="見積書">
          <Icon k="doc" />
          <span data-t="見積書">見積書</span>
        </NavItem>,
        // 納品書（#266、docs/96）：見積書と請求書の間。モジュール sales で使う機能「{deliveryNote}」の会社だけ（既定 営業力オン・現場力オフ）
        sales && uses(me, "deliveryNote") ? (
          <NavItem key="delivery-notes" to="/delivery-notes" className={onA("deliveryNotes")} title={L.deliveryNote} data-testid="nav-delivery-notes">
            <AreaIcon a="deliveryNotes" />
            <span data-t={L.deliveryNote}>{L.deliveryNote}</span>
          </NavItem>
        ) : null,
        <NavItem key="invoices" to="/invoices" className={onA("invoices")} title="請求書">
          <Icon k="inv" />
          <span data-t="請求書">請求書</span>
        </NavItem>,
        // 製品の置き場が登録したメニュー（sales：入金。その会社で有効なモジュールのものだけ。2026-09-29 から現場力の会社も）
        ...navAfter(me, "invoices").map((x) => (
          <NavItem key={x.to} to={x.to} className={on} title={x.label} data-testid={`nav-${x.to.slice(1)}`}>
            <svg viewBox="0 0 24 24" aria-hidden="true">{x.icon}</svg>
            <span data-t={x.label}>{x.label}</span>
          </NavItem>
        )),
      ])}
      {group("order", NAV_GROUP_MSG.order, [
        // 外注見積の一覧（#198、docs/79 決定1）：使う機能「{vendorQuote}の取込」の会社だけ（営業力は言葉の切替で「仕入先見積」）
        uses(me, "vendorQuote") ? (
          <NavItem key="vq" to="/vendor-quotes" className={on} title={L.vendorQuote} data-testid="nav-vendor-quotes">
            <Icon k="vq" />
            <span data-t={L.vendorQuote}>{L.vendorQuote}</span>
          </NavItem>
        ) : null,
        // 見積依頼（#242）：使う機能 rfq の会社だけ
        uses(me, "rfq") ? (
          <NavItem key="rfqs" to="/rfqs" className={on} title={RFQ_MSG.listTitle} data-testid="nav-rfqs">
            <AreaIcon a="rfqs" />
            <span data-t={RFQ_MSG.listTitle}>{RFQ_MSG.listTitle}</span>
          </NavItem>
        ) : null,
        // {order}・仕入（#233、docs/93 §4.1・§15.8）：モジュール sales の会社だけ
        sales ? (
          <NavItem key="orders" to="/orders" className={on} title={L.order} data-testid="nav-orders">
            <Icon k="po" />
            <span data-t={L.order}>{L.order}</span>
          </NavItem>
        ) : null,
        sales ? (
          <NavItem key="purchases" to="/purchases" className={onA("purchases")} title={PURCHASE_MSG.menu} data-testid="nav-purchases">
            <Icon k="box" />
            <span data-t={PURCHASE_MSG.menu}>{PURCHASE_MSG.menu}</span>
          </NavItem>
        ) : null,
      ])}
      {group("master", "", [
        <NavItem key="partners" to="/partners" className={on} title={L.customer}>
          <Icon k="user" />
          <span data-t={L.customer}>{L.customer}</span>
        </NavItem>,
        // 送付状（#223 の3回目の前の直し＝★12）：{customer}のすぐ下。送付の記録の一覧（会社の全件）と「送付状作成」。両製品・スマホ版でも出す（作成は PC）
        <NavItem key="cl" to="/cover-letters" className={onA("coverLetters")} title={COVER_LETTER_MSG.menu} data-testid="nav-cover-letters">
          <Icon k="send" />
          <span data-t={COVER_LETTER_MSG.menu}>{COVER_LETTER_MSG.menu}</span>
        </NavItem>,
      ])}
    </>
  );
}

/** 「設定」（左の列は下の段のいちばん上＝お知らせの上。☰ のメニューは業務の項目の下＝今のまま） */
function NavSettingsItem() {
  return (
    <NavItem to="/settings" className={({ isActive }) => (isActive ? "on" : "")} title="設定" data-testid="nav-settings">
      <Icon k="gear" />
      <span data-t="設定">設定</span>
    </NavItem>
  );
}

/**
 * メニューのいちばん下の項目（左の列は「縮小」の上・☰ のメニューは項目の下。同じもの・同じ順番）：「お知らせ」→「ヘルプ」（#206、docs/83 §14）。
 * 更新情報の未読の朱の点は「お知らせ」だけ（「ヘルプ」には付けない）
 */
function NavHelpItems({ unread, onUpdates, onHelp }: { unread: number; onUpdates: (el: HTMLButtonElement) => void; onHelp: (el: HTMLButtonElement) => void }) {
  return (
    <>
      <UpdatesNavButton onOpen={onUpdates} unread={unread} />
      <HelpNavButton onOpen={onHelp} />
    </>
  );
}

/**
 * 上の段にまだ下へ続く項目があるか（2026-10-21 射場さん、docs/10 §9.27 の9）：縦が短く上の段がメニューの中でスクロールするとき、
 * 下の段との境に薄い影を出す（.navmain.is-more）。一番下まで送ると消え、上の段が全部見えているときは出さない。
 * 箱の大きさ（画面の高さ・畳む／開く）・中身（項目の増減）・スクロールのたびに測り直す
 */
function useMoreBelow(ref: React.RefObject<HTMLElement | null>): boolean {
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
      mo.disconnect();
    };
  }, [ref]);
  return more;
}

/**
 * 畳んだメニューの名前の札（墨の札）の位置：乗せた・フォーカスした項目の右 8px・縦の中央を CSS の変数に入れる（札は position:fixed＝メニューの上の段の
 * スクロールの箱に切られない。2026-10-21、docs/10 §9.27）。開いたメニューでは札を出さない（変数は使われない）
 */
function placeNavTip(e: React.SyntheticEvent) {
  const a = (e.target as Element | null)?.closest?.("a,button.navhelp");
  if (!(a instanceof HTMLElement)) return;
  const r = a.getBoundingClientRect();
  a.style.setProperty("--tip-x", `${Math.round(r.right + 8)}px`);
  a.style.setProperty("--tip-y", `${Math.round(r.top + r.height / 2)}px`);
}

/** スマホの幅（docs/10 §9.12 の1）。CSS の @media (max-width:767px) と同じ境目 */
const PHONE_MQ = "(max-width: 767px)";

/**
 * スマホのメニュー（#106、docs/10 §9.12）：☰ で左から出る。幅 300px（画面の 85% まで）・後ろは 45% 暗く。
 *   閉じるのは ×・後ろを押す・Esc・左へスワイプ・項目を押したとき（画面が変わったら閉じる＝Shell）。
 *   読み上げ：role="dialog"・aria-modal・名前「メニュー」。開いたら最初の項目へ、閉じたら ☰ へ（Shell）。
 *   開いている間は後ろの画面を inert にし（Shell）、Tab はメニューの中だけを回る
 */
function NavDrawer({ me, id, badges, onClose, onHelp, onUpdates }: { me: Me; id: string; badges: NavBadges; onClose: () => void; /** 「ヘルプ」（#187）：メニューを閉じてから窓を開く（写しにメニューを入れない） */ onHelp: () => void; /** 「お知らせ」（#206）：メニューを閉じてから窓を開く */ onUpdates: () => void }) {
  const P = productInfo(me.tenant.product);
  const logoHeader = useLogoHeader(P.product); // ダークでは暗い地用（#191 の2回目）
  const b = me.billing;
  const panel = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number; id: number; dx: number } | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => panel.current?.querySelector<HTMLElement>(".navd-list a")?.focus());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Tab") {
        // 後ろの画面は inert だが、念のためメニューの中で回す
        const xs = Array.from(panel.current?.querySelectorAll<HTMLElement>("a[href],button:not(:disabled)") ?? []);
        if (!xs.length) return;
        const i = xs.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey && i <= 0) {
          e.preventDefault();
          xs[xs.length - 1]!.focus();
        } else if (!e.shiftKey && (i === -1 || i === xs.length - 1)) {
          e.preventDefault();
          xs[0]!.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // 左へスワイプで閉じる（指について動き、60px を超えて離したら閉じる。縦の動きが大きければスクロールとみなす）
  const onDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") return;
    swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId, dx: 0 };
  };
  const onMove = (e: React.PointerEvent) => {
    const s = swipe.current;
    if (!s || s.id !== e.pointerId) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      swipe.current = null;
      if (panel.current) panel.current.style.transform = "";
      return;
    }
    s.dx = Math.min(0, dx);
    if (panel.current) panel.current.style.transform = `translateX(${s.dx}px)`;
  };
  const onUp = () => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    if (panel.current) panel.current.style.transform = "";
    if (s.dx < -60) onClose();
  };

  return (
    <div className="navd" data-testid="nav-drawer" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
      <div className="navd-scrim" aria-hidden="true" onClick={onClose} data-testid="nav-scrim" />
      <div className="navd-panel" id={id} ref={panel} role="dialog" aria-modal="true" aria-label="メニュー">
        <div className="navd-head">
          <Link to="/home" className="brand" title={HOME_MSG.menu} onClick={onClose}>
            <img src={logoHeader} alt={P.name} width={320} height={100} />
          </Link>
          <button type="button" className="navd-close" aria-label="閉じる" onClick={onClose}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {me.tenant.name || b.phase === "trial" ? (
          <div className="navd-co">
            {me.tenant.name ? (
              <span className="co">
                <Icon k="building" />
                {me.tenant.name}
              </span>
            ) : null}
            {b.phase === "trial" ? <TrialChip b={b} onClick={onClose} /> : null}
          </div>
        ) : null}
        {/* 項目を押したら閉じる（いまの画面の項目を押したときも） */}
        <nav className="navd-list" aria-label="メニューの項目" onClick={(e) => (e.target as Element).closest("a") && onClose()}>
          <NavItems me={me} badges={badges} />
          <NavSettingsItem />
          {/* お知らせ（#206）・ヘルプ（#187）：いちばん下。左の列と同じ項目（左の列は「縮小」の上） */}
          <NavHelpItems unread={badges.updates ?? 0} onUpdates={onUpdates} onHelp={onHelp} />
        </nav>
      </div>
    </div>
  );
}

/**
 * 明細の画面のメニュー（#169、docs/10 §9.21）：明細を入力する画面（Route の印 lineEntry＝見積書・請求書・{vendorQuote}の取込）は畳んで始める。
 * 開いた／畳んだは端末ごと（localStorage。明細の画面の分だけ）。ほかの画面はその人が手で決めた幅（覚えない＝今までどおり）
 */
const LINE_NAV_KEY = "gb.nav.lines";
function readLineNavMin(): boolean {
  try {
    return localStorage.getItem(LINE_NAV_KEY) !== "open";
  } catch {
    return true;
  }
}
function writeLineNavMin(min: boolean): void {
  try {
    localStorage.setItem(LINE_NAV_KEY, min ? "min" : "open");
  } catch {
    /* 覚えられない端末（プライベート・ブロック）は、この画面の中だけ */
  }
}
/**
 * ログイン後の枠：上（題字・会社名・「アカウント」メニュー）＋左のメニュー（116px／畳むと 64px。#139・#169）。スマホ（768px 未満）は ☰ のメニュー（#106）。
 * 2026-10-21 射場さん（docs/10 §9.27）：PC版は上の帯とメニューを画面に固定（本文だけスクロール。文書のスクロールは今のまま）・メニューは2段
 *   （上の段＝業務の項目。縦が短いとメニューの中でスクロール／下の段＝設定・お知らせ・ヘルプ・«。いつも画面の下）。
 *   free＝固定しない画面（帳票デザインのエディタ＝全面の作業の画面。docs/10 §9.27 の8）
 *   autoMin＝開いている間は縮小する画面（#290、docs/102 §2.4：案件の工程表・全体の工程表＝表を広く使う）。利用者の設定（userMin）は書き換えない。
 *     その画面で「展開」を押したら、その画面にいる間は展開のまま（画面が変わったら忘れる）
 */
export function Shell({ me, children, badges = {}, lineEntry = false, free = false, autoMin = false }: { me: Me; children: React.ReactNode; badges?: NavBadges; lineEntry?: boolean; free?: boolean; autoMin?: boolean }) {
  // 取得のお知らせ（#225 ★4）：送ったメールの閲覧ページが開かれたら右下に通知（1分ごと・PASSIVE）
  useMailNotices();
  // {orderAck}・紙で受け取りたい のお知らせ（#233。sales の会社だけ）
  useOrderNotices(me.tenant.modules.includes("sales"));
  usePaymentNoticeNotices(me.tenant.modules.includes("sales"));
  const [drawer, setDrawer] = useState(false);
  const burger = useRef<HTMLButtonElement>(null);
  const drawerId = useId();
  const pcOnly = usePcOnly();
  const loc = useLocation();
  const [userMin, setUserMin] = useState(false);
  const [lineMin, setLineMin] = useState(readLineNavMin);
  /** autoMin の画面で「展開」を押した画面（パス）。画面が変わったら忘れる */
  const [autoOpenAt, setAutoOpenAt] = useState<string | null>(null);
  useEffect(() => setAutoOpenAt((v) => (v === loc.pathname ? v : null)), [loc.pathname]);
  const navMin = lineEntry ? lineMin : autoMin ? autoOpenAt !== loc.pathname : userMin;
  // 上の段の続きの影（docs/10 §9.27 の9）
  const navMainRef = useRef<HTMLDivElement>(null);
  const navMore = useMoreBelow(navMainRef);
  const toggleNav = () => {
    if (!lineEntry && autoMin) return setAutoOpenAt(navMin ? loc.pathname : null);
    if (!lineEntry) return setUserMin((v) => !v);
    writeLineNavMin(!navMin);
    setLineMin(!navMin);
  };
  const b = me.billing;
  /** ヘルプの窓（#187：左の列のいちばん下と ☰ のメニューから。前は上の帯の「？」） */
  const support = useSupport();
  /**
   * お知らせの窓（#206）。開くたびに作り直す（key）＝開いた時点の未読で「新着」を出し、見た印を1回付ける。
   * 閉じる動きのあいだは描いておく（Sheet）。updFrom＝開いた所（「ヘルプで見る」のあと「ヘルプ」の窓を閉じたらそこへ戻る）
   */
  const [upd, setUpd] = useState<{ open: boolean; n: number }>({ open: false, n: 0 });
  const updFrom = useRef<HTMLElement | null>(null);
  const openUpdates = (from: HTMLElement | null) => {
    updFrom.current = from;
    setUpd((x) => ({ open: true, n: x.n + 1 }));
  };
  useRipple();
  // 会社の言葉（027）：メニューの「顧客」と、決まり文の {customer} に
  setCurrentLabels(me.tenant.labels);
  // 製品（049）：ログイン後は会社の製品が正。上の帯・題・favicon を切り替え、本番でホストと食い違えばその製品のドメインへ移す
  setCurrentProduct(me.tenant.product);
  const P = productInfo(me.tenant.product);
  const logoHeader = useLogoHeader(P.product); // ダークでは暗い地用（#191 の2回目）
  useEffect(() => {
    if (redirectToProductHost(me.tenant.product)) return;
    warnProductMismatch(me.tenant.product);
    applyBranding(me.tenant.product);
  }, [me.tenant.product]);

  // スマホのメニュー：画面が変わったら（項目を押したら）閉じる。幅が 768px 以上になっても閉じる
  const closeDrawer = useRef(() => {
    setDrawer(false);
    requestAnimationFrame(() => burger.current?.focus());
  }).current;
  useEffect(() => {
    setDrawer((v) => {
      if (v) requestAnimationFrame(() => burger.current?.focus());
      return false;
    });
  }, [loc.pathname]);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE_MQ);
    if (!mq) return;
    const onChange = () => {
      if (!mq.matches) setDrawer(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // 更新情報の未読（#204）：「お知らせ」に朱の点（#206。☰ の点にも数える）。読み直しは人の操作に数えない（PASSIVE）
  const updates = useUpdates();
  const allBadges: NavBadges = { ...badges, updates: updates.data?.unread ?? 0 };
  const badgeSum = (badges.schedule ?? 0) + (allBadges.updates ?? 0);
  // 画面に戻ってきたとき（放置・ほかのアプリ・ロックのあと）に枠ごと描き直させる（iPhone の Safari で真っ白になる。S-00009 の確かめ、lib/repaint.ts）
  const appRef = useRef<HTMLDivElement>(null);
  useEffect(() => (appRef.current ? repaintOnReturn(appRef.current) : undefined), []);

  return (
    <div className={"app" + (navMin ? " navmin" : "") + (free ? " navfree" : "")} data-product={P.product} ref={appRef}>
      <header className="top" inert={drawer || undefined}>
        {/* スマホだけ（CSS）：☰。数のしるしがあれば右上に朱の点（docs/10 §9.12 の5） */}
        <button
          type="button"
          ref={burger}
          className="burger"
          aria-label="メニュー"
          aria-expanded={drawer}
          aria-controls={drawerId}
          onClick={() => setDrawer(true)}
          data-testid="nav-burger"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          {badgeSum > 0 ? <i className="bdot" aria-hidden="true" /> : null}
        </button>
        <Link to="/home" className="brand" title={HOME_MSG.menu}>
          {/* ロゴ（docs/10 §4.3）。広い幅は 角印＋題字、600px 以下は角印だけ（CSS で切替。スマホの ☰ の横は題字つき）。製品で差し替え（営業力は青 B2、docs/10 §4.3.1） */}
          <img className="wide" src={logoHeader} alt={P.name} width={320} height={100} />
          <img className="narrow" src={P.assets.logoMark} alt={P.name} width={100} height={100} />
        </Link>
        {me.tenant.name ? (
          <span className="co">
            <Icon k="building" />
            {me.tenant.name}
          </span>
        ) : null}
        {/* 横断検索（#224、docs/90 §7）：PC は帯の中央の欄（⌘K）・スマホは ☰ の右の虫眼鏡（CSS の order で並べる） */}
        <SearchBox onHelp={(el) => support.open(el)} />
        <span className="sp" />
        {b.phase === "trial" ? <TrialChip b={b} title={b.trialEndsAt ? `${trialLastDay(b.trialEndsAt)}まで。料金プランを表示` : "料金プランを見る"} /> : null}
        {/* お問い合わせ（#124 の 3a）の「？」は #187 で左の列のいちばん下の「ヘルプ」へ（上の帯は ロゴ・会社名・お試し・アカウント） */}
        <AccountMenu me={me} />
      </header>
      <nav className="nav" aria-label="メニュー" inert={drawer || undefined} onPointerOver={placeNavTip} onFocus={placeNavTip}>
        {/* 上の段（業務。縦が短いとこの中だけスクロール＝docs/10 §9.27 の4） */}
        <div className={"navmain" + (navMore ? " is-more" : "")} ref={navMainRef} data-testid="nav-main">
          <NavItems me={me} badges={allBadges} />
        </div>
        {/* 下の段（いつも画面の下）：設定・お知らせ（#206）・ヘルプ（#187。開いている画面と端末を添えて送る）とメニューを小さくする切替（#139） */}
        <div className="navfoot" data-testid="nav-foot">
          <NavSettingsItem />
          <NavHelpItems unread={allBadges.updates ?? 0} onUpdates={openUpdates} onHelp={support.open} />
          {/* メニューを小さくする切替（#139）：アイコンだけ。名前は aria-label・title */}
          <button
            type="button"
            className="navtgl"
            onClick={toggleNav}
            data-testid="nav-toggle"
            aria-label={navMin ? "メニュー展開" : "メニュー縮小"}
            title={navMin ? "メニュー展開" : "メニュー縮小"}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">{navMin ? <path d="M13 17l5-5-5-5M6 17l5-5-5-5" /> : <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />}</svg>
          </button>
        </div>
      </nav>
      <main className="main" inert={drawer || undefined}>
        {b.readOnly ? (
          <div className="next readonly" role="status">
            <span>
              <b>{STOP_MSG.readOnly.bandTitle}</b>
              {STOP_MSG.readOnly.bandBody}
            </span>
            {pcOnly.hidden("/account/plan") ? null : <Link className="btn" to="/account/plan">料金プラン表示</Link>}
          </div>
        ) : null}
        <div className="mainbody">{children}</div>
        <SiteFooter />
      </main>
      {drawer ? (
        <NavDrawer
          me={me}
          id={drawerId}
          badges={allBadges}
          onClose={closeDrawer}
          onHelp={() => {
            closeDrawer();
            // メニューが消えてから写す（2フレーム待つ）。閉じたら ☰ へ戻る
            requestAnimationFrame(() => requestAnimationFrame(() => support.open(burger.current)));
          }}
          onUpdates={() => {
            closeDrawer();
            // メニューが閉じて ☰ にフォーカスが戻ってから開く（閉じたら ☰ へ戻る）
            requestAnimationFrame(() => requestAnimationFrame(() => openUpdates(burger.current)));
          }}
        />
      ) : null}
      {upd.n > 0 ? (
        <UpdatesDialog
          key={upd.n}
          open={upd.open}
          onClose={() => {
            setUpd((x) => ({ ...x, open: false }));
            // 押したボタン（☰ から開いたときは ☰）へ戻す。Safari はマウスで押したボタンにフォーカスを付けない＝シートが覚える「開いた所」が body になるので、ここで明示して戻す（「ヘルプ」の窓と同じ）
            requestAnimationFrame(() => {
              const from = updFrom.current;
              const a = document.activeElement;
              if (from && document.contains(from) && (!a || a === document.body || a.closest(".gsheet"))) from.focus({ preventScroll: true });
            });
          }}
          onHelp={(id) => {
            // お知らせの窓を閉じてから「ヘルプ」の窓でその記事を開く（お知らせの窓は写しに入らない＝data-nocapture）
            setUpd((x) => ({ ...x, open: false }));
            requestAnimationFrame(() => support.open(updFrom.current, id));
          }}
        />
      ) : null}
      {support.dialog}
      {/* ログイン直後に1回だけ「次回からパスキーでログイン」（#113） */}
      <PasskeyPrompt />
    </div>
  );
}

export { Icon };
